<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import StreamService from '@/services/StreamService'
import IconBase from '@/components/icon/IconBase.vue'
import IconCamera from '@/components/icon/IconCamera.vue'

const store = useStore()

const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id');
		}
	},
	compactMode: {
		required: true
	},
	mediaPulse: {
		type: Object,
		default() { return {}; }
	},
	obs: {
		type: Boolean,
		default: false
	}
})

const scopeAlive = ref(true),
	hasStreamThumb = ref(null),
	thumbUrl = ref(null),
	savedSchedulerConfig = ref(null),
	scheduleTimeActive = ref(false),
	thumbCreated = ref(false),
	meta = ref(null),
	liveStreamCountdownSecLeft = ref(null),
	thumbContainer = ref()


onMounted(async () => {
	const isWowzaRegion = !!props.stream?.region?.v2;
	let hostname = 'dvr.castr.io'
	let prefixPath = ''
	if (isWowzaRegion) {
		hostname = 'preview.castr.io'
		prefixPath = 'preview/'
	}

	thumbUrl.value = `https://${hostname}/`
	if (prefixPath) {
		thumbUrl.value += prefixPath
	}

	const regionDepId = props.stream?.region?.identifierHaxr
	thumbUrl.value += `${props.stream.key}/preview.mp4?&region=${regionDepId}`

	// setTimeout(this.setThumbnail.bind(this), 1000);
	if (props.stream.type === 'scheduled') {
		await setupScheduling()
	}

	if (props.stream.type === 'live') {
		// Use cached store to prevent fetching metadata for every stream card
		const metaInfo = await store.dispatch('StreamCache/getMetadata', { 
			streamId: props.stream._id,
			videoId: false 
		});
		meta.value = metaInfo
		if (meta.value?.countdownDate) {
			liveStreamCountdownSecLeft.value = Math.max(new Date(meta.value.countdownDate) - Date.now(), 0);
		}
	}
})
onUnmounted(() => {
	scopeAlive.value = false
})

watch(() => props.mediaPulse, () => {
	const hostId = props.mediaPulse?.hostId
	if (hostId) {
		const staticPrefix = props.mediaPulse?.staticPrefix;
		const anc = document.createElement('a')
		anc.href = thumbUrl.value
		if (/\w+-\d+/gi.test(hostId) || props.stream.pullUrl) {
			anc.hostname = `${hostId}.castr.io`;
			if (!anc.pathname.includes('static/') && staticPrefix) {
				anc.pathname = anc.pathname.replace(props.stream.key, `static/${props.mediaPulse?.name}`);
			}
		}
		thumbUrl.value = anc.href;
		if (!thumbCreated.value && thumbUrl.value) {
			setThumbnail()
		}

		if (props.obs && !thumbUrl.value.includes('_preview')) {
			thumbUrl.value = thumbUrl.value.replace('/preview.mp4', '_preview/preview.mp4')
		}
	}
})

const isPortraitResolution = computed(() => {
	const alive = props.mediaPulse?.enabled && props.mediaPulse.alive;
	if (props.mediaPulse && Number(props.mediaPulse.width) < Number(props.mediaPulse.height)) {
		return true
	}

	return false
})

const onStreamCountdownTick = (last, isFinished) => {
	if (isFinished) {
		liveStreamCountdownSecLeft.value = null
	}
}

const setThumbnail = () => {
	if (!scopeAlive.value) return;

	const thumbContainerInstatce = thumbContainer.value;
	// prepare a new video element to overalp existing ones
	let mediaElement;
	const streamEnabled = props.stream?.enabled;
	const streamAlive = props.mediaPulse?.alive;

	if (streamEnabled && streamAlive && thumbContainerInstatce) {
		const isWowzaRegion = !!props.stream?.region?.v2;
		if (isWowzaRegion) {
			const thumbUrl = getVideoThumbUrl();
			if (thumbUrl) {
				thumbCreated.value = true
				mediaElement = createImgInstance(thumbUrl, () => {
					thumbContainerInstatce.appendChild(mediaElement);
				})
			}
		} else {
			const videoSrc = getVideoPreviewUrl();
			mediaElement = createVideoInstance(videoSrc);
			if (mediaElement) {
				thumbCreated.value = true;
				thumbContainerInstatce.appendChild(mediaElement);
			
			}
		}
	}
	// add few secs delay to make the video chunk appear
	if (mediaElement) {
		setTimeout(() => {
			mediaElement.className = mediaElement.className + ' ready';

			// reschedule thumb setup
			scheduleSetup.call(this, mediaElement.tagName.toLowerCase());
		}, 3000);
	} else {
		// reschedule thumb setup
		scheduleSetup.call(this);
	}

	function scheduleSetup(thumbType = 'video') {
		// setTimeout(setThumbnail.bind(this), 8000)

		if (!thumbContainer.value) return;

		// remove old video thumbs
		const streamEnabled = props.stream.enabled;

		// recheck if placeholder text has to appear instead of video thumb
		const hasStreamThumbnail = streamEnabled && props.mediaPulse?.alive;
		hasStreamThumb.value = hasStreamThumbnail;

		const mediaEls = thumbContainer.value?.getElementsByTagName(thumbType);
		Array.prototype.forEach.call(mediaEls, mediaEl => {
			// remove video if stream is offline or is older video
			if (!hasStreamThumb || mediaEl !== mediaElement) {
				if (thumbContainer.value) {
					thumbContainer.value.removeChild(mediaEl)
				}
			}
		})
	}
}
const getVideoPreviewUrl = () => {
	if (!props.mediaPulse?.alive) return;
	const randomkey = Math.random().toString().slice(2)
	const vidUrl = thumbUrl.value + `&rand=${randomkey}`
	return vidUrl
}
const setupScheduling = async () => {
	// Use cached store to prevent duplicate calls
	const schedulerConfig = await store.dispatch('StreamCache/getSchedulerConfig', props.stream._id) || {};
	savedSchedulerConfig.value = schedulerConfig;
}

function createVideoInstance(videoSrc) {
	if (!videoSrc) return;

	if (props.obs && !videoSrc.includes('_preview')) {
		videoSrc = videoSrc.replace('/preview.mp4', '_preview/preview.mp4')
	}

	const vid = document.createElement('video');
	vid.src = videoSrc;
	vid.className = 'stream-preview' + computedClasses.value + (isPortraitResolution.value && props.obs ? ' portrait-preview' : '');

	return vid;
}

function createImgInstance(imgSrc, callback) {
	if (!imgSrc) return;
	const img = document.createElement('img');
	img.src = imgSrc;
	img.className = 'stream-preview';
	img.onload = () => void callback();
	return img;
}
const computedClasses = computed(() => props.compactMode ? 'w-full rounded-md' : 'w-full rounded-t-lg')

const computedPlaceholder = computed(() => {
	if (!props.stream.enabled) return 'Disabled Stream'
	if (props.mediaPulse?.alive) return 'Waiting for frames'
	if (props.stream.type === 'ipcam') return 'No camera source'
	if (props.stream.type === 'vod') return 'Static Media Files'
	if ((props.stream.type === 'scheduled') || (props.stream.type === 'live' && liveStreamCountdownSecLeft)) return 'Stream will go Live in'
	return 'Waiting for stream'

})
</script>
<template>
	<div :class="props.compactMode ? 'line-view': 'grid-view'">
		<div id="thumb-container" ref="thumbContainer"
			:class="[
				computedClasses,
				{
					'portrait-thumb-container bg-surface-2': isPortraitResolution && obs,
					'bg-gradient-to-b from-surface-darker to-black': !(isPortraitResolution && obs),
				}
			]"
			class="aspect-video relative h-full overflow-hidden"
		>
			<div
				v-show="!hasStreamThumb"
				class="placeholder w-full h-full flex flex-col items-center justify-center text-surface-5 absolute top-0 left-0"
				:class="props.compactMode ? 'gap-0.5 px-1 text-center' : 'gap-2'"
			>
				<template v-if="!thumbCreated">
					<icon-base
						:width="props.compactMode ? 16 : 28"
						:height="props.compactMode ? 16 : 28"
						:width-view-box="16"
						:height-view-box="14"
						class="opacity-50"
					>
						<icon-camera />
					</icon-base>
					<span :class="props.compactMode ? 'text-[10px] leading-tight' : ''">{{ computedPlaceholder }}</span>
					<span v-if="!props.stream.enabled && !props.compactMode" class="text-sm text-surface-7">Please enable</span>
				</template>
			</div>
		</div>
	</div>
</template>
<style>
.line-view .ready {
	@apply rounded-b-lg
}

.portrait-thumb-container {
	display: flex;
	justify-content: center;
	align-items: center;
}

.portrait-preview {
	width: 100% !important;
	height: 100% !important;
	object-fit: contain;
}
</style>
